// Become an Installer Partner — full premium lead-gen page with a working enquiry form.
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Handshake, TrendingUp, Star, ShieldCheck, BookOpen, MapPin,
  ListChecks, Send, Building2, User, Mail, Phone, Briefcase, Clock,
  MessageSquare, CheckCircle2, Sparkles,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/layout/Reveal";
import { submitLead } from "@/lib/api";
import { toast } from "sonner";

const BENEFITS = [
  { icon: TrendingUp, t: "Growing demand",         b: "More Kiwi homes are upgrading from gas and electric storage to efficient heat pump hot water." },
  { icon: Star,       t: "Quality product",        b: "Install the premium NeoPower Black Shield — a system you can stand behind." },
  { icon: Handshake,  t: "Lead opportunities",     b: "Access AeroMax-generated install opportunities as our customer pipeline grows." },
  { icon: BookOpen,   t: "Brand support",          b: "Professional brand assets, training and customer-coordination support." },
  { icon: ShieldCheck,t: "Clear standards",        b: "Documented installation playbook and consistent quality expectations." },
  { icon: MapPin,     t: "Regional growth",        b: "Long-term opportunity as AeroMax expands across NZ regions." },
];

const WHO = [
  "Experienced plumbers or hot water installers",
  "Professional, tidy workmanship",
  "Customer-first communication",
  "Relevant licences and qualifications",
  "Reliable scheduling and follow-through",
  "Willingness to align with AeroMax installation standards",
];

const PROCESS = [
  { n: "01", t: "Submit your interest",          b: "Fill in the partner enquiry form below — takes about 2 minutes." },
  { n: "02", t: "AeroMax reviews your details",  b: "Our partnerships team reviews your trade, experience, and region." },
  { n: "03", t: "Introductory conversation",     b: "Phone or video catch-up to discuss your business and how we work." },
  { n: "04", t: "Standards & service alignment", b: "Walk-through of our install standards, product training and customer flow." },
  { n: "05", t: "Regional opportunities",        b: "As demand grows in your area, we connect you with install opportunities." },
];

const REGIONS = ["Auckland", "Waikato", "Bay of Plenty", "Hawke's Bay", "Manawatū", "Wellington", "Canterbury", "Other / Multi-region"];
const TRADES = ["Plumber", "Hot water installer", "Electrician", "Heat pump specialist", "Plumbing + electrical business", "Other"];
const EXPERIENCE = ["Less than 2 years", "2–5 years", "5–10 years", "10+ years"];

function Hero() {
  return (
    <section data-testid="partner-hero" className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20 sm:pt-20 sm:pb-24">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <Handshake className="h-3 w-3 text-[#10B981]" /> Become an Installer Partner
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Partner with <span className="text-[#10B981]">AeroMax Energy.</span>
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl">
            Join a growing clean-energy installation network helping Kiwi homes transition to efficient hot water heat pump systems.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#apply" data-testid="partner-cta-apply" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30">
              Apply to Partner <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/installer-network" data-testid="partner-cta-network" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 text-white px-7 py-3.5 font-semibold transition-all">
              See Our Network
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PartnerForm() {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    region: "", trade: "", experience: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.business_name.trim()) e.business_name = "Please enter your business name.";
    if (!form.contact_name.trim())  e.contact_name  = "Please enter a contact name.";
    if (!form.email.trim())         e.email         = "Please enter an email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.phone.trim())         e.phone         = "Please enter a phone number.";
    if (!form.region)               e.region        = "Please select a region.";
    if (!form.trade)                e.trade         = "Please select a trade.";
    if (!form.experience)           e.experience    = "Please select years of experience.";
    if (!form.message.trim())       e.message       = "Please add a short message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete all required fields to apply.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        type: "partner_enquiry",
        name: form.contact_name,
        email: form.email,
        phone: form.phone,
        region: form.region,
        notes: `Business: ${form.business_name}\nTrade: ${form.trade}\nExperience: ${form.experience}\n\nMessage:\n${form.message}`,
        consent: true,
      });
      setSubmitted(true);
      toast.success("Thanks — your application has been received. We'll be in touch.");
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card data-testid="partner-form-success" className="rounded-3xl border-[#10B981]/30 bg-gradient-to-br from-white to-[#F0FDF4] p-8 sm:p-10 shadow-lg shadow-emerald-500/10">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#10B981] text-white">
          <CheckCircle2 className="h-6 w-6" strokeWidth={2.4} />
        </span>
        <h3 className="font-heading mt-5 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight">Thanks — your application has been received.</h3>
        <p className="mt-3 text-[#475569] leading-relaxed max-w-xl">
          Our partnerships team will review your details and be in touch within a few business days to schedule an introductory conversation.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/installer-network" className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] hover:bg-[#1E293B] text-white px-6 py-3 font-semibold">
            See Our Network <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] hover:border-[#0B132B] text-[#0B132B] px-6 py-3 font-semibold">
            Back to Home
          </Link>
        </div>
      </Card>
    );
  }

  const f = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };

  return (
    <Card data-testid="partner-form" className="rounded-3xl border-[#E2E8F0] bg-white p-7 sm:p-9 shadow-xl shadow-emerald-500/5">
      <div className="mb-6">
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Partner enquiry</span>
        <h3 className="font-heading mt-2 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight">Apply to join the AeroMax network.</h3>
        <p className="mt-2 text-sm text-[#475569]">All fields are required.</p>
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <FieldRow icon={Building2} label="Business name *" error={errors.business_name}>
          <Input data-testid="partner-business" value={form.business_name} onChange={(e) => f("business_name", e.target.value)} placeholder="Your company name" />
        </FieldRow>
        <FieldRow icon={User} label="Contact name *" error={errors.contact_name}>
          <Input data-testid="partner-contact" value={form.contact_name} onChange={(e) => f("contact_name", e.target.value)} placeholder="Your full name" />
        </FieldRow>
        <FieldRow icon={Mail} label="Email *" error={errors.email}>
          <Input data-testid="partner-email" type="email" value={form.email} onChange={(e) => f("email", e.target.value)} placeholder="you@company.co.nz" />
        </FieldRow>
        <FieldRow icon={Phone} label="Phone *" error={errors.phone}>
          <Input data-testid="partner-phone" type="tel" value={form.phone} onChange={(e) => f("phone", e.target.value)} placeholder="021 …" />
        </FieldRow>
        <FieldRow icon={MapPin} label="Region *" error={errors.region}>
          <Select data-testid="partner-region" value={form.region} onChange={(v) => f("region", v)} options={REGIONS} placeholder="Select region" />
        </FieldRow>
        <FieldRow icon={Briefcase} label="Trade / service type *" error={errors.trade}>
          <Select data-testid="partner-trade" value={form.trade} onChange={(v) => f("trade", v)} options={TRADES} placeholder="Select trade" />
        </FieldRow>
        <FieldRow icon={Clock} label="Years of experience *" error={errors.experience} className="sm:col-span-2">
          <Select data-testid="partner-experience" value={form.experience} onChange={(v) => f("experience", v)} options={EXPERIENCE} placeholder="Select experience" />
        </FieldRow>
        <FieldRow icon={MessageSquare} label="Short message *" error={errors.message} className="sm:col-span-2">
          <textarea
            data-testid="partner-message"
            value={form.message}
            onChange={(e) => f("message", e.target.value)}
            rows={4}
            placeholder="Tell us a little about your business, regions you cover, and why you'd like to partner."
            className="w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
          />
        </FieldRow>

        <div className="sm:col-span-2 mt-2">
          <Button
            type="submit"
            data-testid="partner-submit"
            disabled={submitting}
            className="w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white py-6 font-semibold text-base"
          >
            <Send className="h-4 w-4 mr-1.5" />
            {submitting ? "Sending..." : "Apply to Become an Installer Partner"}
          </Button>
          <p className="mt-3 text-[11px] text-[#475569] text-center">
            Your details are reviewed by the AeroMax partnerships team. We'll respond within a few business days.
          </p>
        </div>
      </form>
    </Card>
  );
}

function FieldRow({ icon: Icon, label, error, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs flex items-center gap-1.5 mb-1.5 text-[#0B132B] font-semibold">
        {Icon && <Icon className="h-3.5 w-3.5 text-[#10B981]" />} {label}
      </Label>
      {children}
      {error && <p className="text-[11px] text-red-600 mt-1.5 font-medium" role="alert">{error}</p>}
    </div>
  );
}

function Select({ value, onChange, options, placeholder, ...rest }) {
  return (
    <select
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (<option key={o} value={o}>{o}</option>))}
    </select>
  );
}

export default function BecomeInstallerPartner() {
  return (
    <div className="bg-white">
      <Hero />

      {/* Why partner */}
      <section data-testid="partner-why" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal className="max-w-3xl">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Why partner with AeroMax</span>
            <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              Build your business with a <span className="text-[#10B981]">premium clean-energy brand.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="h-full rounded-2xl bg-white border border-[#E2E8F0] p-7 hover:border-[#10B981]/40 hover:shadow-lg hover:shadow-emerald-500/8 hover:-translate-y-0.5 transition-all">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10B981]/12 text-[#10B981] ring-1 ring-[#10B981]/20">
                    <b.icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <h3 className="font-heading mt-5 text-lg font-bold text-[#0B132B] leading-snug">{b.t}</h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">{b.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who we're looking for */}
      <section data-testid="partner-who" className="py-20 sm:py-24 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">Who we're looking for</span>
            <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              The kind of installers <span className="text-[#10B981]">we want to work with.</span>
            </h2>
            <p className="mt-5 text-lg text-[#475569] leading-relaxed">
              We're building a network of professionals who take pride in their work, communicate well with customers, and align with AeroMax's installation standards.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="rounded-3xl bg-white border border-[#E2E8F0] p-7 sm:p-9 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <ListChecks className="h-4 w-4 text-[#10B981]" />
                <p className="text-xs uppercase tracking-widest font-semibold text-[#475569]">Looking for</p>
              </div>
              <ul className="space-y-3">
                {WHO.map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#10B981]/12 text-[#10B981] ring-1 ring-[#10B981]/25 shrink-0">
                      <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.6} />
                    </span>
                    <p className="text-sm text-[#0B132B] leading-relaxed">{t}</p>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How the partnership process works */}
      <section data-testid="partner-process" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal className="max-w-3xl">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981]">How it works</span>
            <h2 className="font-heading mt-3 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              A clear, <span className="text-[#10B981]">no-pressure</span> process.
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={i} delay={i * 60}>
                <div data-testid={`partner-step-${i + 1}`} className="relative h-full rounded-2xl bg-white border border-[#E2E8F0] p-6 hover:border-[#10B981]/40 hover:shadow-lg hover:shadow-emerald-500/8 hover:-translate-y-0.5 transition-all">
                  <span className="font-heading text-2xl font-bold text-[#E2E8F0] num-tab leading-none">{p.n}</span>
                  <h3 className="font-heading mt-4 text-base font-bold text-[#0B132B] leading-snug">{p.t}</h3>
                  <p className="mt-2 text-xs text-[#475569] leading-relaxed">{p.b}</p>
                  {i < PROCESS.length - 1 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-3.5 -translate-y-1/2 h-7 w-7 items-center justify-center rounded-full bg-[#10B981] text-white shadow-lg shadow-emerald-500/40">
                      <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" data-testid="partner-apply" className="scroll-mt-28 py-20 sm:py-24 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <Reveal className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.22em] uppercase">
              <Sparkles className="h-3 w-3" /> Partner enquiry form
            </span>
            <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              Ready to apply? <span className="text-[#10B981]">It takes 2 minutes.</span>
            </h2>
            <p className="mt-4 text-[#475569] max-w-xl mx-auto">
              Tell us about your business and we'll be in touch.
            </p>
          </Reveal>
          <Reveal delay={80}>
            <PartnerForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
